import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import AuthLayout, { useAuthFormStyles } from "../../components/AuthLayout";
import { checkPasswordResetToken, resetPassword } from "../../services/passwordRecovery.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const MIN = 8;

/** Elige la contraseña nueva a partir del enlace del correo (doc 0446). */
const ResetPassword = () => {
  const classes = useAuthFormStyles();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);

  const [status, setStatus] = useState("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!/^[a-f0-9]{64}$/i.test(token)) {
      setStatus("invalid");
      return undefined;
    }
    checkPasswordResetToken(token)
      .then(({ data }) => !cancelled && setStatus(data?.status || "invalid"))
      .catch(() => !cancelled && setStatus("invalid"));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const tooShort = password.length > 0 && password.length < MIN;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSave = password.length >= MIN && confirm === password && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      await resetPassword(token, password);
      setStatus("done");
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_RESET_TOKEN_EXPIRED") setStatus("expired");
      else if (code === "ERR_RESET_TOKEN_USED") setStatus("used");
      else if (code === "ERR_RESET_TOKEN_INVALID") setStatus("invalid");
      else toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const linkTo = (to, label) => (
    <Link component={RouterLink} to={to} style={{ textAlign: "center", fontSize: 14 }}>
      {label}
    </Link>
  );

  if (status === "checking") {
    return (
      <AuthLayout subtitle={i18n.t("passwordRecovery.checking")}>
        <CircularProgress style={{ alignSelf: "center" }} />
      </AuthLayout>
    );
  }

  if (status === "done") {
    return (
      <AuthLayout title={i18n.t("passwordRecovery.doneTitle")} subtitle={i18n.t("passwordRecovery.doneBody")}>
        <CheckCircleOutlineIcon style={{ fontSize: 48, color: "#15803D", alignSelf: "center" }} />
        <Button
          component={RouterLink}
          to="/login"
          variant="contained"
          color="primary"
          size="large"
          className={classes.submit}
        >
          {i18n.t("passwordRecovery.goLogin")}
        </Button>
      </AuthLayout>
    );
  }

  if (status !== "valid") {
    return (
      <AuthLayout
        title={i18n.t(`passwordRecovery.${status}Title`)}
        subtitle={i18n.t(`passwordRecovery.${status}Body`)}
      >
        <Button
          component={RouterLink}
          to="/forgot-password"
          variant="contained"
          color="primary"
          size="large"
          className={classes.submit}
        >
          {i18n.t("passwordRecovery.requestNew")}
        </Button>
        {linkTo("/login", i18n.t("passwordRecovery.backToLogin"))}
      </AuthLayout>
    );
  }

  const eye = (
    <InputAdornment position="end">
      <IconButton aria-label="toggle password visibility" onClick={() => setShow(!show)} edge="end">
        {show ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <AuthLayout title={i18n.t("passwordRecovery.resetTitle")} subtitle={i18n.t("passwordRecovery.resetSubtitle")}>
      <form noValidate onSubmit={submit} className={classes.form}>
        <TextField
          variant="outlined"
          className={classes.field}
          fullWidth
          autoFocus
          type={show ? "text" : "password"}
          autoComplete="new-password"
          label={i18n.t("passwordRecovery.newPassword")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={tooShort}
          helperText={i18n.t("passwordRecovery.minLength")}
          InputProps={{ endAdornment: eye }}
        />
        <TextField
          variant="outlined"
          className={classes.field}
          fullWidth
          type={show ? "text" : "password"}
          autoComplete="new-password"
          label={i18n.t("passwordRecovery.confirmPassword")}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch}
          helperText={mismatch ? i18n.t("passwordRecovery.mismatch") : " "}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={!canSave}
          className={classes.submit}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : i18n.t("passwordRecovery.save")}
        </Button>
      </form>
      {linkTo("/login", i18n.t("passwordRecovery.backToLogin"))}
    </AuthLayout>
  );
};

export default ResetPassword;
